import { FocusModeContext } from '@/contexts/focus-mode.context'
import { useCallback, useEffect, useState, type ReactNode } from 'react'

interface FocusModeProviderProps {
  children: ReactNode
}

/**
 * Provider for Focus Mode state
 * Wrap your app with this provider to share focus mode state across all components
 */
export const FocusModeProvider = ({ children }: FocusModeProviderProps) => {
  const [isFocusMode, setIsFocusMode] = useState(false)

  const enterFocusMode = useCallback(() => {
    setIsFocusMode(true)

    // Enter browser fullscreen
    if (document.documentElement.requestFullscreen)
      document.documentElement.requestFullscreen().catch(() => {
        // Fullscreen request failed, but focus mode still works
      })
  }, [])

  const exitFocusMode = useCallback(() => {
    setIsFocusMode(false)

    // Exit browser fullscreen if active
    if (document.fullscreenElement && document.exitFullscreen)
      document.exitFullscreen().catch(() => {
        // Exit fullscreen failed
      })
  }, [])

  const toggleFocusMode = useCallback(() => {
    if (isFocusMode) exitFocusMode()
    else enterFocusMode()
  }, [isFocusMode, enterFocusMode, exitFocusMode])

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // F11 to toggle focus mode
      if (event.key === 'F11') {
        event.preventDefault()
        toggleFocusMode()
      }

      // Escape to exit focus mode (unless it already cancelled a form or closed a popover)
      if (event.key === 'Escape' && isFocusMode && !event.defaultPrevented) {
        event.preventDefault()
        exitFocusMode()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isFocusMode, toggleFocusMode, exitFocusMode])

  // Handle browser fullscreen change (e.g., user presses Escape in browser fullscreen)
  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement && isFocusMode) {
        // User exited fullscreen via browser, also exit focus mode
        setIsFocusMode(false)
      }
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [isFocusMode])

  return (
    <FocusModeContext.Provider
      value={{
        isFocusMode,
        toggleFocusMode,
        enterFocusMode,
        exitFocusMode,
      }}
    >
      {children}
    </FocusModeContext.Provider>
  )
}

export default FocusModeProvider
